import { produce } from "immer";
import { isEqual } from "lodash";
import { Form, FormResponse, Guest, Purchase, PurchaseStatus } from "types";
import create from "zustand";

// The type is Partial<Purchase> because initially, not all attributes are present
export interface PurchaseResponseStore extends Partial<Purchase> {
    purchase: Partial<Purchase>;
    actions: {
        setPurchase: (purchase: Purchase) => void;
        removeItem: (index: number) => void;
        resetState: () => void;
    };
    getters: {
        getForm: (index: number) => Form | undefined;
        getPackageForm: () => Form | undefined;
        getFormResponse: (index: number) => FormResponse | undefined;
    };
    uiCachedState?: {
        items: Array<{ guests: Guest[]; form?: Form; formResponse: FormResponse }>;
    };
}

const defaultState = {
    customerName: "",
    customerEmail: "",
    phone: "",
    seller: { id: "" },
    currency: "USD",
    items: [],
    lineItems: [],
    status: PurchaseStatus.DRAFT,
};

// This store holds the current purchase data as returned by the API.
// Note: Changes in this store should not be used to trigger updates elsewhere.
export const usePurchaseResponseStore = create<PurchaseResponseStore>((set, get) => ({
    purchase: defaultState,
    uiCachedState: undefined,

    actions: {
        setPurchase: (newPurchase: Purchase) => {
            set(
                produce((draft) => {
                    draft.purchase = newPurchase;

                    if (!draft.uiCachedState) {
                        draft.uiCachedState = { items: [] };
                    }

                    // 3. Sync cached values item by item
                    for (let index = 0; index < newPurchase.items.length; index++) {
                        const newItem = newPurchase.items[index];
                        const cachedItems = draft.uiCachedState.items;

                        // Ensure cachedItems[i] exists
                        while (cachedItems.length <= index) {
                            cachedItems.push(getDefaultCachedItem());
                        }

                        if (newItem.form && !cachedItems[index].form) {
                            cachedItems[index].form = newItem.form;
                        }

                        if (!isEqual(cachedItems[index].formResponse, newItem.formResponse)) {
                            cachedItems[index].formResponse = newItem.formResponse;
                        }
                    }
                }),
            );
        },

        // Removes the cached form/formResponse at `index` so a new item that later reuses this
        // array index doesn't inherit a stale entry left over from the removed item.
        removeItem: (index: number) => {
            set(
                produce((draft) => {
                    draft.uiCachedState?.items?.splice(index, 1);
                }),
            );
        },

        resetState: () => {
            set(() => ({
                purchase: structuredClone(defaultState),
                uiCachedState: undefined,
            }));
        },
    },

    getters: {
        getForm: (index) => {
            const cachedForm = get().uiCachedState?.items?.[index]?.form;
            const currentForm = get().purchase.items?.[index]?.form;

            return cachedForm ?? currentForm;
        },

        getPackageForm: () => {
            return get().purchase.form;
        },

        getFormResponse: (index) => {
            const cachedFormResponse = get().uiCachedState?.items?.[index]?.formResponse;
            const currentFormResponse = get().items?.[index]?.formResponse;

            return cachedFormResponse ?? currentFormResponse;
        },
    },
}));

const getDefaultCachedItem = () => ({
    form: undefined,
    formResponse: undefined,
});
